import * as React from 'react';
import type { SimpleFileUploadProps, SimpleFileUploadRef, FileChangeEvent } from './types';

// Extend JSX intrinsic elements to include the web component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'simple-file-upload': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          'public-key'?: string;
          tag?: string;
          multiple?: boolean;
          'max-file-size'?: number;
          'max-files'?: number;
          accept?: string;
        },
        HTMLElement
      >;
    }
  }
}

/**
 * React wrapper for SimpleFileUpload web component
 * Automatically loads the script and provides optimal drag and drop support
 */
export const SimpleFileUpload = React.forwardRef<SimpleFileUploadRef, SimpleFileUploadProps>(
  (
    {
      publicKey,
      tag,
      multiple = true,
      maxFileSize = 5242880,
      maxFiles = 5,
      accept,
      onChange,
      onUploadDone,
      onReset,
      className,
      style,
    },
    ref
  ) => {
    const [isMounted, setIsMounted] = React.useState(false);
    const [scriptLoaded, setScriptLoaded] = React.useState(false);
    const [scriptError, setScriptError] = React.useState<string | null>(null);
    const webComponentRef = React.useRef<HTMLElement>(null);

    // Set mounted state
    React.useEffect(() => {
      setIsMounted(true);
    }, []);

    // Load the script
    React.useEffect(() => {
      if (!isMounted) return;
      // Check if the web component is already defined
      if (customElements.get('simple-file-upload')) {
        setScriptLoaded(true);
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      script.src = 'https://cdn.simplefileupload.com/simple-file-upload.latest.js';
      script.async = true;

      const handleLoad = () => {
        setScriptLoaded(true);
      };

      const handleError = () => {
        setScriptError('Failed to load SimpleFileUpload web component from CDN');
      };

      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);

      document.body.appendChild(script);

      // Cleanup
      return () => {
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);
        // Don't remove the script tag - once the web component is registered,
        // it stays in customElements and removing the script would cause
        // unnecessary refetches if the component remounts
      };
    }, [isMounted]);

    // Expose the reset method via ref
    React.useImperativeHandle(ref, () => ({
      reset: () => {
        if (webComponentRef.current) {
          (webComponentRef.current as any).reset();
        }
      },
    }));

    // Set up event handlers
    React.useEffect(() => {
      const element = webComponentRef.current;
      if (!element || !scriptLoaded) return;

      // Event handlers
      const handleChange = (event: Event) => {
        const customEvent = event as CustomEvent<FileChangeEvent>;
        if (onChange) {
          onChange(customEvent.detail);
        }
        
        // Also trigger onUploadDone if the action is 'uploadDone'
        if (customEvent.detail.action === 'uploadDone' && onUploadDone) {
          onUploadDone(customEvent.detail);
        }
      };

      const handleReset = (event: Event) => {
        if (onReset) {
          onReset();
        }
      };

      // Add event listeners
      element.addEventListener('change', handleChange);
      element.addEventListener('reset', handleReset);

      // Cleanup
      return () => {
        element.removeEventListener('change', handleChange);
        element.removeEventListener('reset', handleReset);
      };
    }, [onChange, onUploadDone, onReset, scriptLoaded]);

    // During SSR and initial client render, show a placeholder
    if (!isMounted || !scriptLoaded) {
      // Multiple mode: show button
      if (multiple) {
        return (
          <button
            className={className}
            style={{ 
              backgroundColor: '#e5e7eb',
              color: '#111827',
              border: 'none',
              padding: '10px 16px',
              fontSize: '14px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              lineHeight: '16px',
              fontFeatureSettings: 'normal',
              display: 'inline-block',
              verticalAlign: 'middle',
              ...style
            }}
            disabled
          >
            Upload File
          </button>
        );
      }
      
      // Single mode: show drop zone
      return (
        <div
          className={className}
          style={{
            backgroundColor: '#f2f3f4',
            color: '#333',
            border: '1px dashed #e5e5e5',
            borderRadius: '14px',
            padding: '1.25rem 1rem',
            fontSize: '15px',
            textAlign: 'center',
            minWidth: '320px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            cursor: 'pointer',
            boxSizing: 'border-box',
            ...style
          }}
        >
          Choose file
        </div>
      );
    }

    if (scriptError) {
      return <div style={{ color: 'red', padding: '10px' }}>Error: {scriptError}</div>;
    }

    // Direct render without wrapper for optimal drag and drop
    return (
      <simple-file-upload
        ref={webComponentRef}
        public-key={publicKey}
        tag={tag}
        multiple={multiple}
        max-file-size={maxFileSize}
        max-files={maxFiles}
        accept={accept}
        className={className}
        style={style}
      />
    );
  }
);

SimpleFileUpload.displayName = 'SimpleFileUpload';
